import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { UI_TIMEOUTS } from '../../config/defaults';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  animated?: boolean;
  animationDelay?: number; // milliseconds between each line
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ 
  content, 
  className = '',
  animated = false,
  animationDelay = UI_TIMEOUTS.ANIMATION_DELAY
}) => {
  // Convert \n characters to proper markdown paragraph breaks
  const processedContent = content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0) // Remove empty lines
    .join('\n\n'); // Join with double newlines for markdown paragraphs

  // Split content into blocks for animation
  const contentBlocks = useMemo(() => {
    if (!animated) return [processedContent];
    
    return processedContent
      .split('\n\n')
      .filter(block => block.trim().length > 0);
  }, [processedContent, animated]);

  const [visibleBlocks, setVisibleBlocks] = useState<number>(animated ? 0 : contentBlocks.length);

  useEffect(() => {
    if (!animated || contentBlocks.length === 0) return;

    setVisibleBlocks(0);
    
    const timer = setInterval(() => {
      setVisibleBlocks(prev => {
        if (prev >= contentBlocks.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, animationDelay);

    return () => clearInterval(timer);
  }, [contentBlocks, animated, animationDelay]);

  const customComponents = {
    h1: ({ children }: any) => (
      <h1 className="text-2xl font-medium text-gray-800 mb-4">{children}</h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="text-xl font-medium text-gray-800 mb-3">{children}</h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-lg font-medium text-gray-800 mb-2">{children}</h3>
    ),
    h4: ({ children }: any) => (
      <h4 className="text-base font-medium text-gray-800 mb-2">{children}</h4>
    ),
    p: ({ children }: any) => (
      <p className="text-md text-gray-700 mb-4 leading-relaxed">{children}</p>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold text-gray-800">{children}</strong>
    ),
    em: ({ children }: any) => (
      <em className="text-blue-600 font-medium">{children}</em>
    ),
    ul: ({ children }: any) => (
      <ul className="space-y-2 mb-4">{children}</ul>
    ),
    li: ({ children }: any) => (
      <li className="text-base text-gray-700">{children}</li>
    ),
    // Handle feature blocks with special formatting
    blockquote: ({ children }: any) => (
      <div className="grid gap-4 text-left p-6 border border-gray-200 rounded-lg mb-4">
        {children}
      </div>
    ),
  };

  if (!animated) {
    return (
      <div className={`prose prose-gray max-w-none ${className}`}>
        <ReactMarkdown components={customComponents}>
          {processedContent}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className={`prose prose-gray max-w-none ${className}`}>
      {contentBlocks.slice(0, visibleBlocks).map((block, index) => (
        <div key={index}>
          <ReactMarkdown components={customComponents}>
            {block}
          </ReactMarkdown>
        </div>
      ))}
    </div>
  );
};
